<script setup lang="ts">
/**
 * UI-kit component
 * Category: Fields
 * Name: AppDatePicker
 */
import { DatePicker } from 'v-calendar'

import type { AppDatePickerProps } from '../types/interfaces'

import { faCalendar } from '@fortawesome/pro-regular-svg-icons'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppIcon } from '@shared/ui/icons'
import { BtnIcon } from '@shared/ui/buttons'

import { useAppDatePicker } from '../composables/app-date-picker.hook'
import 'v-calendar/style.css'

defineOptions({
  name: 'AppDatePicker',
})

const props = withDefaults(defineProps<AppDatePickerProps>(), {
  value: null,
  label: '',
  required: false,
  placeholder: 'Select the date',
  disabled: false,
  state: 'static',
  minDate: null,
})

const emit = defineEmits<{
  change: [value: string]
}>()

const {
  refIsOpen,
  refControl,
  refTrigger,
  refDropdown,
  refDropdownStyles,
  refDate,
  refMinDate,
  displayText,
  hasValue,
  toggle,
  toggleWithKeyboard,
  close,
  change,
  clear,
} = useAppDatePicker({
  props,
  onChange: (value: string) => emit('change', value),
})
</script>

<template>
  <div class="app-date-picker">
    <AppText
      v-if="label"
      name="body2"
      color="slate-900"
      tag="div"
    >
      {{ label }}<span
        v-if="required"
        class="app-date-picker__required"
      >*</span>
    </AppText>

    <div
      ref="refControl"
      class="app-date-picker__control"
    >
      <div
        ref="refTrigger"
        role="button"
        :tabindex="disabled ? -1 : 0"
        :aria-disabled="disabled"
        class="app-date-picker__trigger"
        :class="{
          'app-date-picker__trigger--clearable': hasValue,
          'app-date-picker__trigger--error': state === 'error',
        }"
        @click="toggle"
        @keydown.enter.prevent="toggleWithKeyboard"
        @keydown.space.prevent="toggleWithKeyboard"
      >
        <AppIcon
          class="app-date-picker__icon"
          :icon="faCalendar"
          color="slate-500"
        />
        <span
          v-if="!hasValue"
          class="app-date-picker__placeholder"
        >
          {{ placeholder }}
        </span>
        <span
          v-else
          class="app-date-picker__text"
        >
          {{ displayText }}
        </span>
        <BtnIcon
          v-if="hasValue"
          class="app-date-picker__clear"
          view="transparent"
          size="small"
          :icon="faXmark"
          @click.stop="clear"
        />
      </div>

      <Teleport to="body">
        <div
          v-if="refIsOpen"
          ref="refDropdown"
          class="app-date-picker__dropdown"
          :style="refDropdownStyles"
          @keydown.esc="close"
        >
          <DatePicker
            :model-value="refDate"
            :min-date="refMinDate"
            mode="date"
            borderless
            :locale="{ id: 'en', firstDayOfWeek: 2, masks: { title: 'MMM YYYY', input: 'MMM D, YYYY' } }"
            @update:model-value="change"
          />
        </div>
      </Teleport>
    </div>
  </div>
</template>

<style lang="scss" scoped src="../styles/_app-date-picker.scss" />
